import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Stack,
    Divider,
    Paper,
    alpha,
    Alert,
    Button,
    CircularProgress,
    Skeleton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip as MuiTooltip,
    ToggleButton,
    ToggleButtonGroup,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RepeatIcon from '@mui/icons-material/Repeat';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DevicesIcon from '@mui/icons-material/Devices';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import InsightsIcon from '@mui/icons-material/Insights';
import TableChartIcon from '@mui/icons-material/TableChart';
import {
    aggregateActivityMetas,
    buildBehaviorInsights,
    buildPerAttemptSummary,
    buildSessionBrowserTeacherSummary,
    buildSessionReplayTeacherSummary,
} from './studentAssignmentTrackingUtils';
import {
    WORK_SESSION_LOADING_LABEL,
    WORK_SESSION_MODAL_TITLE,
    WORK_SESSION_REFRESH_LABEL,
} from './studentAssignmentTrackingStatic';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
} from 'recharts';
import * as Sentry from '@sentry/react';
import SentryWrap from 'components/Sentry';
import { apis } from 'utils/lib/apis';
import AssignmentSessionReplayPlayer from './AssignmentSessionReplayPlayer';

const TEAL = '#03989e';
const TEAL_LIGHT = '#4db6ac';
const MUTED = '#5c6b7a';

function num(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function formatDuration(sec) {
    const s = num(sec);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r ? `${m}m ${r}s` : `${m}m`;
}

function formatWhen(iso) {
    if (!iso || typeof iso !== 'string') return '—';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

function normalizeSubmissionActivityLogsBundle(raw) {
    if (!raw || typeof raw !== 'object') {
        return { sources: [], aggregated: null, submissionId: null };
    }
    let body = raw;
    if (raw.success === true && raw.data && typeof raw.data === 'object') {
        body = raw.data;
    }
    if (body.data && typeof body.data === 'object' && Array.isArray(body.data.sources)) {
        body = body.data;
    }
    const sources = Array.isArray(body.sources) ? body.sources : [];
    const aggregated =
        body.aggregated && typeof body.aggregated === 'object' ? body.aggregated : null;
    return {
        sources,
        aggregated,
        submissionId: body.submissionId != null ? String(body.submissionId) : null,
    };
}

function normalizeActivityLogPayload(raw) {
    if (!raw || typeof raw !== 'object') return {};
    if (raw.success === true && raw.data && typeof raw.data === 'object') {
        return normalizeActivityLogPayload(raw.data);
    }
    if (
        typeof raw.copy_attempt_count === 'number' ||
        typeof raw.paste_attempt_count === 'number' ||
        typeof raw.duration_sec === 'number'
    ) {
        return raw;
    }
    if (raw.data && typeof raw.data === 'object') return normalizeActivityLogPayload(raw.data);
    if (raw.payload && typeof raw.payload === 'object') return normalizeActivityLogPayload(raw.payload);
    if (raw.meta_data && typeof raw.meta_data === 'object') return normalizeActivityLogPayload(raw.meta_data);
    return raw;
}

function SnippetBlock({ title, count, snippets }) {
    const list = Array.isArray(snippets) ? snippets : [];
    const groupedSnippets = useMemo(() => {
        const counts = new Map();
        list.forEach((entry) => {
            const text = typeof entry === 'string' ? entry : String(entry ?? '');
            if (!text) return;
            counts.set(text, (counts.get(text) || 0) + 1);
        });
        return Array.from(counts.entries()).map(([text, occurrences]) => ({ text, occurrences }));
    }, [list]);
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1a2b3c' }}>
                    {title}
                </Typography>
                <Typography variant="caption" color={MUTED} fontWeight={600}>
                    ({count})
                </Typography>
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
            {groupedSnippets.length ? (
                <Stack spacing={1.25}>
                    {groupedSnippets.map(({ text, occurrences }) => {
                        const MAX_SNIPPET_LENGTH = 80;
                        const isTruncated = text.length > MAX_SNIPPET_LENGTH;
                        const displayText = isTruncated ? `${text.slice(0, MAX_SNIPPET_LENGTH).trimEnd()}...` : text;
                        return (
                            <Paper
                                key={text}
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    bgcolor: alpha(TEAL, 0.04),
                                    borderColor: alpha(TEAL, 0.2),
                                    fontFamily: 'ui-monospace, monospace',
                                    fontSize: 13,
                                }}
                            >
                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                    <MuiTooltip title={text} placement="top-start" disableHoverListener={!isTruncated}>
                                        <Typography
                                            component="span"
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                color: '#1a2b3c',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {displayText}
                                        </Typography>
                                    </MuiTooltip>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: MUTED, fontWeight: 700, whiteSpace: 'nowrap' }}
                                    >
                                        × {occurrences}
                                    </Typography>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            ) : (
                <Typography color={MUTED} variant="body2">
                    No captured text (counts may still be above).
                </Typography>
            )}
        </Box>
    );
}

function StudentAssignmentTrackingSkeleton({ isMobile }) {
  const metricCardSx = {
    flex: { xs: '1 1 calc(50% - 6px)', sm: '1 1 140px' },
    minWidth: { xs: 'calc(50% - 6px)', sm: 140 },
    maxWidth: { xs: '100%', sm: 200 },
    p: 1.75,
    borderRadius: 2,
    border: '1px solid',
    borderColor: alpha(TEAL, 0.12),
    bgcolor: '#fff',
  };

  return (
    <Box aria-busy="true" aria-label={WORK_SESSION_LOADING_LABEL}>
      <Skeleton variant="text" width={isMobile ? '85%' : 320} height={22} sx={{ mb: 2 }} />
      <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: { xs: 2.25, sm: 3 } }}>
        {Array.from({ length: 7 }).map((_, i) => (
          <Paper key={i} elevation={0} sx={metricCardSx}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Skeleton variant="circular" width={22} height={22} />
              <Skeleton variant="text" width={72} height={14} />
            </Stack>
            <Skeleton variant="text" width={56} height={isMobile ? 28 : 32} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="70%" height={14} />
          </Paper>
        ))}
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minWidth: 0,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(TEAL, 0.12)}`,
            bgcolor: '#fff',
          }}
        >
          <Skeleton variant="text" width={140} height={24} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="90%" height={16} sx={{ mb: 1.5 }} />
          <Skeleton
            variant="rounded"
            height={isMobile ? 220 : 300}
            sx={{ borderRadius: 2 }}
          />
        </Paper>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minWidth: 0,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(TEAL, 0.12)}`,
            bgcolor: '#fff',
          }}
        >
          <Skeleton variant="text" width={180} height={24} sx={{ mb: 2 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" width={48} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={72} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={72} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" width={52} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={72} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={72} />
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </Box>
    );
}

function DevicesSessionsAccordion({ summary }) {
    if (!summary?.hasContent) return null;

    const {
        workSessionCount,
        workSessionSwitches,
        deviceLabels,
        deviceChangeCount,
        primaryDevice,
    } = summary;

    return (
        <Accordion
            disableGutters
            elevation={0}
            defaultExpanded={false}
            sx={{
                mt: 1.5,
                '&:before': { display: 'none' },
                borderRadius: '12px !important',
                border: `1px solid ${alpha(TEAL, 0.18)}`,
                overflow: 'hidden',
                bgcolor: alpha(TEAL, 0.03),
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: TEAL }} />}
                sx={{
                    minHeight: 48,
                    px: 1.25,
                    '& .MuiAccordionSummary-content': {
                        my: 1,
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                    },
                }}
            >
                <DevicesIcon sx={{ fontSize: 20, color: TEAL, flexShrink: 0 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1a2b3c' }}>
                    Devices & work sessions
                </Typography>
                {summary.summaryLine ? (
                    <Typography variant="caption" color={MUTED} sx={{ ml: { sm: 0.5 } }}>
                        {summary.summaryLine}
                    </Typography>
                ) : null}
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.25 }}>
                <Stack spacing={1.5}>
                    {deviceLabels.length > 0 ? (
                        <Box>
                            <Typography variant="caption" color={MUTED} fontWeight={600} display="block" sx={{ mb: 0.75 }}>
                                {deviceLabels.length > 1 ? 'Devices or browsers seen' : 'Likely device used'}
                            </Typography>
                            <Stack spacing={0.75}>
                                {deviceLabels.map((label) => (
                                    <Typography
                                        key={label}
                                        variant="body2"
                                        sx={{
                                            color: '#1a2b3c',
                                            pl: 1,
                                            borderLeft: `3px solid ${alpha(TEAL, 0.45)}`,
                                        }}
                                    >
                                        {label}
                                        {label === primaryDevice && deviceLabels.length > 1 ? (
                                            <Typography component="span" variant="caption" color={MUTED} sx={{ ml: 0.75 }}>
                                                (most recent)
                                            </Typography>
                                        ) : null}
                                    </Typography>
                                ))}
                            </Stack>
                            {deviceChangeCount > 0 ? (
                                <Typography variant="caption" color={MUTED} sx={{ mt: 0.75, display: 'block' }}>
                                    Student switched device or browser {deviceChangeCount} time
                                    {deviceChangeCount === 1 ? '' : 's'} during this work.
                                </Typography>
                            ) : null}
                        </Box>
                    ) : null}
                    {workSessionCount > 0 ? (
                        <Box>
                            <Typography variant="caption" color={MUTED} fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                                Work sessions
                            </Typography>
                            <Typography variant="body2" color="#1a2b3c">
                                {workSessionCount === 1
                                    ? 'Student worked in one continuous visit for this view.'
                                    : `Student opened the assignment ${workSessionCount} separate times.`}
                            </Typography>
                            {workSessionSwitches > 0 ? (
                                <Typography variant="caption" color={MUTED} sx={{ mt: 0.5, display: 'block' }}>
                                    They left and came back {workSessionSwitches} time
                                    {workSessionSwitches === 1 ? '' : 's'} (new tab, new day, or sign-in again).
                                </Typography>
                            ) : null}
                        </Box>
                    ) : null}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}

function SessionReplayAccordion({ replaySummary }) {
    const [playerOpen, setPlayerOpen] = useState(false);
    const [activeSession, setActiveSession] = useState(null);

    if (!replaySummary?.hasContent) return null;
    const { sessionCount, chunkCount, sessions } = replaySummary;

    const openPlayer = (session) => {
        setActiveSession(session);
        setPlayerOpen(true);
    };

    const closePlayer = () => {
        setPlayerOpen(false);
        setActiveSession(null);
    };

    return (
        <>
            <Accordion
                disableGutters
                elevation={0}
                defaultExpanded={false}
                sx={{
                    mt: 1.5,
                    '&:before': { display: 'none' },
                    borderRadius: '12px !important',
                    border: `1px solid ${alpha(TEAL, 0.18)}`,
                    overflow: 'hidden',
                    bgcolor: alpha(TEAL, 0.03),
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: TEAL }} />}
                    sx={{
                        minHeight: 48,
                        px: 1.25,
                        '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center', gap: 1 },
                    }}
                >
                    <ReplayIcon sx={{ fontSize: 20, color: TEAL, flexShrink: 0 }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1a2b3c' }}>
                        Session recordings
                    </Typography>
                    <Typography variant="caption" color={MUTED}>
                        {sessionCount} session{sessionCount === 1 ? '' : 's'}, {chunkCount} part
                        {chunkCount === 1 ? '' : 's'}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.25 }}>
                    <Stack spacing={1}>
                        {sessions.map((session) => (
                            <Button
                                key={session.sessionId || session.label}
                                size="small"
                                variant="contained"
                                startIcon={<PlayArrowIcon />}
                                onClick={() => openPlayer(session)}
                                sx={{
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    bgcolor: TEAL,
                                    '&:hover': { bgcolor: '#027a80' },
                                }}
                            >
                                Watch {session.label}
                                {session.chunks.length > 1
                                    ? ` (${session.chunks.length} parts)`
                                    : ''}
                            </Button>
                        ))}
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Dialog open={playerOpen} onClose={closePlayer} maxWidth="lg" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pr: 1,
                    }}
                >
                    <Typography variant="h6" fontWeight={700}>
                        {activeSession?.label || 'Session recording'}
                    </Typography>
                    <IconButton onClick={closePlayer} aria-label="Close replay">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: '#f4f7f8', minHeight: 320 }}>
                    {activeSession ? (
                        <AssignmentSessionReplayPlayer
                            chunkUrls={activeSession.chunks}
                            title={activeSession.label}
                        />
                    ) : null}
                </DialogContent>
            </Dialog>
        </>
    );
}

function BehaviorInsightsPanel({ insights, isMobile, sessionBrowserSummary, replaySummary }) {
    if (!insights?.length && !sessionBrowserSummary?.hasContent && !replaySummary?.hasContent) return null;
    const severityColor = {
        warning: { bg: alpha('#ed6c02', 0.08), border: alpha('#ed6c02', 0.35), icon: '#e65100' },
        info: { bg: alpha(TEAL, 0.08), border: alpha(TEAL, 0.28), icon: TEAL },
        success: { bg: alpha('#2e7d32', 0.08), border: alpha('#2e7d32', 0.28), icon: '#2e7d32' },
    };
    return (
        <Paper
            elevation={0}
            sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(TEAL, 0.2)}`,
                bgcolor: '#fff',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <InsightsIcon sx={{ color: TEAL, fontSize: 22 }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a2b3c' }}>
                    Teacher insights
                </Typography>
            </Stack>
            {insights?.length ? (
                <Stack spacing={1}>
                    {insights.map((item) => {
                        const palette = severityColor[item.severity] || severityColor.info;
                        return (
                            <Box
                                key={`${item.severity}-${item.title}`}
                                sx={{
                                    p: 1.25,
                                    borderRadius: 1.5,
                                    bgcolor: palette.bg,
                                    border: `1px solid ${palette.border}`,
                                }}
                            >
                                <Typography
                                    variant={isMobile ? 'body2' : 'subtitle2'}
                                    fontWeight={700}
                                    sx={{ color: palette.icon, mb: 0.25 }}
                                >
                                    {item.title}
                                </Typography>
                                <Typography variant="body2" color={MUTED}>
                                    {item.detail}
                                </Typography>
                            </Box>
                        );
                    })}
                </Stack>
            ) : null}
            <DevicesSessionsAccordion summary={sessionBrowserSummary} />
            <SessionReplayAccordion replaySummary={replaySummary} />
        </Paper>
    );
}

function AttemptBreakdownTable({ rows, isMobile }) {
    if (!rows?.length) return null;
    return (
        <Paper
            elevation={0}
            sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(TEAL, 0.15)}`,
                bgcolor: '#fff',
                overflow: 'auto',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <TableChartIcon sx={{ color: TEAL, fontSize: 22 }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a2b3c' }}>
                    Per submission
                </Typography>
            </Stack>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? 12 : 13 }}>
                <Box component="thead">
                    <Box component="tr" sx={{ borderBottom: `1px solid ${alpha(TEAL, 0.15)}` }}>
                        {['Attempt', 'Time', 'Copy', 'Paste', 'Other tabs', 'Clicked away'].map((h) => (
                            <Box
                                key={h}
                                component="th"
                                sx={{ textAlign: 'left', py: 0.75, pr: 1, color: MUTED, fontWeight: 600 }}
                            >
                                {h}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {rows.map((row) => (
                        <Box
                            key={row.logId}
                            component="tr"
                            sx={{ borderBottom: `1px solid ${alpha('#000', 0.06)}` }}
                        >
                            <Box component="td" sx={{ py: 0.75, pr: 1, fontWeight: 600, color: '#1a2b3c' }}>
                                {row.label}
                            </Box>
                            <Box component="td" sx={{ py: 0.75, pr: 1 }}>{formatDuration(row.duration_sec)}</Box>
                            <Box component="td" sx={{ py: 0.75, pr: 1 }}>{row.copy_attempt_count}</Box>
                            <Box component="td" sx={{ py: 0.75, pr: 1 }}>{row.paste_attempt_count}</Box>
                            <Box component="td" sx={{ py: 0.75, pr: 1 }}>{row.page_visibility_change_count}</Box>
                            <Box component="td" sx={{ py: 0.75, pr: 1 }}>{row.window_blur_count}</Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Paper>
    );
}

export default function StudentAssignmentTrackingModal({
    open,
    onClose,
    logId,
    attemptLabel,
    submissionId = null,
    allLogSources = [],
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [meta, setMeta] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [viewMode, setViewMode] = useState('single');
    const [attemptBreakdown, setAttemptBreakdown] = useState([]);

    const logSources = useMemo(() => {
        const list = Array.isArray(allLogSources) ? allLogSources : [];
        return list.filter((s) => s?.logId);
    }, [allLogSources]);

    const canShowOverall = logSources.length > 1;

    useEffect(() => {
        if (!open) {
            setMeta({});
            setLoadError(null);
            setIsLoading(false);
            setViewMode('single');
            setAttemptBreakdown([]);
            return;
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;

        if (viewMode === 'overall' && !canShowOverall) {
            setViewMode('single');
        }
    }, [open, viewMode, canShowOverall]);

    useEffect(() => {
        if (!open) return;

        let cancelled = false;

        const fetchOne = async (id) => {
            const raw = await apis.get(
                'assignmentsRDS',
                `/activityLog/${encodeURIComponent(String(id))}`
            );
            return normalizeActivityLogPayload(raw);
        };

        (async () => {
            if (viewMode === 'overall') {
                const resolvedSubmissionId =
                    submissionId != null && String(submissionId).trim()
                        ? String(submissionId).trim()
                        : logSources[0]?.submissionId != null
                          ? String(logSources[0].submissionId).trim()
                          : null;

                if (!resolvedSubmissionId && !logSources.length) {
                    setLoadError('No tracked submissions to combine.');
                    setMeta({});
                    setAttemptBreakdown([]);
                    return;
                }
                setIsLoading(true);
                setLoadError(null);
                try {
                    if (resolvedSubmissionId) {
                        const raw = await apis.get(
                            'assignmentsRDS',
                            `/submissions/${encodeURIComponent(resolvedSubmissionId)}/activityLogs`
                        );
                        if (cancelled) return;
                        const bundle = normalizeSubmissionActivityLogsBundle(raw);
                        const validPairs = (bundle.sources || [])
                            .filter((s) => s?.meta && typeof s.meta === 'object')
                            .map((s) => ({
                                source: { logId: s.logId, label: s.label },
                                meta: s.meta,
                            }));
                        if (!validPairs.length) {
                            setLoadError('Could not load activity for all attempts. Try Refresh.');
                            setMeta({});
                            setAttemptBreakdown([]);
                        } else {
                            const validMetas = validPairs.map((p) => p.meta);
                            const validSources = validPairs.map((p) => p.source);
                            setMeta(
                                bundle.aggregated && typeof bundle.aggregated === 'object'
                                    ? bundle.aggregated
                                    : aggregateActivityMetas(validMetas)
                            );
                            setAttemptBreakdown(buildPerAttemptSummary(validSources, validMetas));
                            setLoadError(null);
                        }
                    } else {
                        const pairs = await Promise.all(
                            logSources.map(async (source) => {
                                try {
                                    const metaPayload = await fetchOne(source.logId);
                                    return { source, meta: metaPayload };
                                } catch (err) {
                                    Sentry.captureException(err);
                                    return { source, meta: null };
                                }
                            })
                        );
                        if (cancelled) return;
                        const validPairs = pairs.filter((p) => p.meta && typeof p.meta === 'object');
                        if (!validPairs.length) {
                            setLoadError('Could not load activity for all attempts. Try Refresh.');
                            setMeta({});
                            setAttemptBreakdown([]);
                        } else {
                            const validMetas = validPairs.map((p) => p.meta);
                            const validSources = validPairs.map((p) => p.source);
                            setMeta(aggregateActivityMetas(validMetas));
                            setAttemptBreakdown(buildPerAttemptSummary(validSources, validMetas));
                            setLoadError(null);
                        }
                    }
                } catch (err) {
                    if (cancelled) return;
                    Sentry.captureException(err);
                    setLoadError(err?.message || 'Could not load combined activity. Try Refresh.');
                    setMeta({});
                    setAttemptBreakdown([]);
                } finally {
                    if (!cancelled) setIsLoading(false);
                }
                return;
            }

            if (!logId) return;

            setIsLoading(true);
            setLoadError(null);
            setAttemptBreakdown([]);
            try {
                const payload = await fetchOne(logId);
                if (cancelled) return;
                setMeta(payload);
            } catch (err) {
                if (cancelled) return;
                Sentry.captureException(err);
                setLoadError(err?.message || 'Could not load this attempt’s activity. Try Refresh.');
                setMeta({});
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, logId, submissionId, viewMode, logSources, refreshNonce, canShowOverall]);

    const m = meta && typeof meta === 'object' ? meta : {};

    const barRows = useMemo(
        () => [
            { key: 'copy', name: 'Copy', value: num(m.copy_attempt_count), fill: '#1565c0' },
            { key: 'paste', name: 'Paste', value: num(m.paste_attempt_count), fill: '#6a1b9a' },
            { key: 'visibility', name: 'Other tabs/pages', value: num(m.page_visibility_change_count), fill: '#ef6c00' },
            { key: 'blur', name: 'Clicked away', value: num(m.window_blur_count), fill: '#c62828' },
            { key: 'menu', name: 'Right‑click', value: num(m.context_menu_attempt_count), fill: '#2e7d32' },
        ],
        [m]
    );

    const attemptCountForInsights =
        viewMode === 'overall' ? logSources.length : 1;

    const behaviorInsights = useMemo(
        () => buildBehaviorInsights(m, attemptCountForInsights, viewMode),
        [m, attemptCountForInsights, viewMode]
    );

    const copySnips = Array.isArray(m.copy_content_snippets) ? m.copy_content_snippets : [];
    const pasteSnips = Array.isArray(m.paste_content_snippets) ? m.paste_content_snippets : [];

    const sessionBrowserSummary = useMemo(() => buildSessionBrowserTeacherSummary(m), [m]);
    const replaySummary = useMemo(() => buildSessionReplayTeacherSummary(m), [m]);

    const metricCards = useMemo(() => {
        const cards = [
            {
                key: 'duration',
                label: viewMode === 'overall' ? 'Total time' : 'Time on assignment',
                value: formatDuration(m.duration_sec),
                sub: `${num(m.duration_sec)} sec`,
                icon: <TimerOutlinedIcon sx={{ fontSize: 22 }} />,
            },
            {
                key: 'copy',
                label: 'Copy count',
                value: String(num(m.copy_attempt_count)),
                sub: 'copy / cut actions',
                icon: <ContentCopyIcon sx={{ fontSize: 22 }} />,
            },
            {
                key: 'paste',
                label: 'Paste count',
                value: String(num(m.paste_attempt_count)),
                sub: 'paste actions',
                icon: <ContentPasteIcon sx={{ fontSize: 22 }} />,
            },
            {
                key: 'resume',
                label: 'Resume opens',
                value: String(num(m.resume_count)),
                sub: 'after save / finish later',
                icon: <ReplayIcon sx={{ fontSize: 22 }} />,
            },
            {
                key: 'finish',
                label: 'Finish later',
                value: String(num(m.finish_later_count)),
                sub: 'saved & left',
                icon: <ExitToAppIcon sx={{ fontSize: 22 }} />,
            },
            {
                key: 'visibility',
                label: 'Other tabs or pages',
                value: String(num(m.page_visibility_change_count)),
                sub: 'switched within the app',
                icon: <VisibilityOffIcon sx={{ fontSize: 22 }} />,
            },
            {
                key: 'blur',
                label: 'Clicked away',
                value: String(num(m.window_blur_count)),
                sub: 'another app or window',
                icon: <TouchAppIcon sx={{ fontSize: 22 }} />,
            },
        ];
        if (viewMode === 'overall') {
            cards.push({
                key: 'attempts',
                label: 'Tracked attempts',
                value: String(attemptCountForInsights),
                sub: 'submissions with logs',
                icon: <RepeatIcon sx={{ fontSize: 22 }} />,
            });
        } else {
            cards.push({
                key: 'retake',
                label: 'Retake #',
                value: String(num(m.retake_sessions_count)),
                sub: 'attempt index',
                icon: <RepeatIcon sx={{ fontSize: 22 }} />,
            });
        }
        return cards;
    }, [m, viewMode, attemptCountForInsights]);

    const titleSuffix =
        viewMode === 'overall'
            ? ' · All attempts combined'
            : attemptLabel
              ? ` · ${attemptLabel}`
              : '';
    const showLoadingSkeleton = isLoading && !loadError;

    return (
        <SentryWrap>
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            scroll="paper"
            PaperProps={{
                sx: {
                    m: { xs: 0, sm: 2 },
                    mb: { xs: 0, sm: 6 },
                    width: { xs: '100%', sm: 'auto' },
                    maxHeight: { xs: '100dvh', sm: 'calc(100dvh - 80px)' },
                    borderRadius: { xs: 0, sm: 2 },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: { xs: 1, sm: 1.25 },
                    pr: { xs: 1.5, sm: 1 },
                    pl: { xs: 1.5, sm: 3 },
                    py: { xs: 1.25, sm: 2 },
                    background: `linear-gradient(135deg, ${TEAL} 0%, #02796d 100%)`,
                    color: '#fff',
                }}
            >
                <Box sx={{ minWidth: 0, flex: '1 1 280px', pr: 1 }}>
                    <Typography
                        variant={isMobile ? 'subtitle1' : 'h6'}
                        component="span"
                        fontWeight={700}
                        sx={{
                            display: 'block',
                            lineHeight: 1.25,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                        }}
                        title={`${WORK_SESSION_MODAL_TITLE}${titleSuffix}`}
                    >
                        {WORK_SESSION_MODAL_TITLE}{titleSuffix}
                    </Typography>
                </Box>
                <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ ml: 'auto', flexWrap: 'wrap', minWidth: 0, rowGap: 0.75 }}
                >
                    {canShowOverall ? (
                        <ToggleButtonGroup
                            exclusive
                            value={viewMode}
                            onChange={(_, next) => {
                                if (next != null) setViewMode(next);
                            }}
                            disabled={isLoading}
                            size={isMobile ? 'medium' : 'small'}
                            sx={{
                                flexShrink: 0,
                                bgcolor: alpha('#fff', 0.14),
                                borderRadius: 1.25,
                                overflow: 'hidden',
                                '& .MuiToggleButtonGroup-grouped': {
                                    border: 0,
                                    margin: 0,
                                    '&:not(:first-of-type)': {
                                        marginLeft: 0,
                                        borderLeft: `1px solid ${alpha('#fff', 0.35)}`,
                                        borderRadius: 0,
                                    },
                                    '&:first-of-type': {
                                        borderRadius: '10px 0 0 10px',
                                    },
                                    '&:last-of-type': {
                                        borderRadius: '0 10px 10px 0',
                                    },
                                },
                                '& .MuiToggleButton-root': {
                                    color: '#fff',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                    px: { xs: 1.25, sm: 1.75 },
                                    py: { xs: 0.65, sm: 0.5 },
                                    border: 'none',
                                    '&.Mui-selected': {
                                        bgcolor: '#fff',
                                        color: '#015b52',
                                        '&:hover': {
                                            bgcolor: alpha('#fff', 0.92),
                                        },
                                    },
                                    '&:hover': {
                                        bgcolor: alpha('#fff', 0.2),
                                    },
                                    '&.Mui-disabled': {
                                        borderColor: 'transparent',
                                        '&:not(.Mui-selected)': {
                                            color: alpha('#fff', 0.45),
                                        },
                                        '&.Mui-selected': {
                                            bgcolor: alpha('#fff', 0.92),
                                            color: alpha('#015b52', 0.6),
                                        },
                                    },
                                },
                            }}
                        >
                            <ToggleButton value="single" disabled={!logId}>
                                {isMobile ? 'Attempt' : 'This attempt'}
                            </ToggleButton>
                            <ToggleButton value="overall" disabled={!logSources.length}>
                                Overall
                            </ToggleButton>
                        </ToggleButtonGroup>
                    ) : null}
                    <Button
                        variant="text"
                        size={isMobile ? 'medium' : 'small'}
                        startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                        onClick={() => setRefreshNonce((n) => n + 1)}
                        disabled={
                            isLoading ||
                            (viewMode === 'single' && !logId) ||
                            (viewMode === 'overall' && !logSources.length)
                        }
                        sx={{
                            color: '#015b52',
                            textTransform: 'none',
                            fontWeight: 600,
                            lineHeight: 1.3,
                            flexShrink: 0,
                            minWidth: 'fit-content',
                            px: { xs: 1.25, sm: 1.75 },
                            py: { xs: 0.65, sm: 0.5 },
                            borderRadius: 1.25,
                            border: 'none',
                            bgcolor: '#fff',
                            whiteSpace: 'nowrap',
                            boxShadow: 'none',
                            '& .MuiButton-startIcon': {
                                marginRight: '6px',
                                marginLeft: 0,
                                color: 'inherit',
                            },
                            '&:hover:not(.Mui-disabled)': {
                                bgcolor: alpha('#fff', 0.92),
                            },
                            '&.Mui-disabled': {
                                opacity: 1,
                                color: alpha('#015b52', 0.55),
                                WebkitTextFillColor: alpha('#015b52', 0.55),
                                bgcolor: alpha('#fff', 0.92),
                                '& .MuiButton-startIcon': {
                                    opacity: 1,
                                    color: 'inherit',
                                },
                            },
                        }}
                    >
                        {WORK_SESSION_REFRESH_LABEL}
                    </Button>
                    <IconButton onClick={onClose} sx={{ color: '#fff', flexShrink: 0 }} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 'calc(16px + env(safe-area-inset-bottom))', sm: 'calc(24px + env(safe-area-inset-bottom))' },
                    px: { xs: 1.5, sm: 3 },
                    bgcolor: '#f4f7f8',
                }}
            >
                {loadError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {loadError}
                    </Alert>
                ) : null}
                {showLoadingSkeleton ? (
                    <StudentAssignmentTrackingSkeleton isMobile={isMobile} />
                ) : (
                    <>
                <Typography variant="body2" color={MUTED} sx={{ mb: 2 }}>
                    {viewMode === 'overall' ? 'First session started' : 'Session started'}:{' '}
                    <strong>{formatWhen(m.assignment_session_started_at)}</strong>
                    {m.tracking_version != null ? (
                        <>
                            {' '}
                            · tracking v{m.tracking_version}
                        </>
                    ) : null}
                </Typography>

                <BehaviorInsightsPanel
                    insights={behaviorInsights}
                    isMobile={isMobile}
                    sessionBrowserSummary={sessionBrowserSummary}
                    replaySummary={replaySummary}
                />

                {viewMode === 'overall' ? (
                    <AttemptBreakdownTable rows={attemptBreakdown} isMobile={isMobile} />
                ) : null}

                <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: { xs: 2.25, sm: 3 } }}>
                    {metricCards.map((card) => (
                        <Paper
                            key={card.key}
                            elevation={0}
                            sx={{
                                flex: { xs: '1 1 calc(50% - 6px)', sm: '1 1 140px' },
                                minWidth: { xs: 'calc(50% - 6px)', sm: 140 },
                                maxWidth: { xs: '100%', sm: 200 },
                                p: 1.75,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: alpha(TEAL, 0.2),
                                bgcolor: '#fff',
                                transition: '0.2s',
                                '&:hover': { borderColor: TEAL_LIGHT, boxShadow: 2 },
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ color: TEAL, mb: 0.5 }}>
                                {card.icon}
                                <Typography variant="caption" color={MUTED} fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                                    {card.label}
                                </Typography>
                            </Stack>
                            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800} sx={{ color: '#1a2b3c' }}>
                                {card.value}
                            </Typography>
                            <Typography variant="caption" color={MUTED}>
                                {card.sub}
                            </Typography>
                        </Paper>
                    ))}
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="stretch">
                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(TEAL, 0.15)}`,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, color: '#1a2b3c' }}>
                            Activity events
                        </Typography>
                        <Typography variant="caption" color={MUTED} display="block" sx={{ mb: 1 }}>
                            {viewMode === 'overall'
                                ? 'Summed counts across all tracked submissions'
                                : 'Copy & paste bars match the counts on the cards above'}
                        </Typography>
                        <Box sx={{ width: '100%', height: { xs: 220, sm: 280, md: 300 } }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={barRows}
                                    margin={{ top: 8, right: isMobile ? 0 : 8, left: isMobile ? -20 : 0, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.06)} />
                                    <XAxis dataKey="name" tick={{ fontSize: isMobile ? 10 : 11, fill: MUTED }} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: MUTED }} />
                                    <Tooltip contentStyle={{ borderRadius: 8 }} formatter={(v) => [v, 'Count']} />
                                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                        {barRows.map((e) => (
                                            <Cell key={e.key} fill={e.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(TEAL, 0.15)}`,
                            bgcolor: '#fff',
                            // maxHeight: { xs: 'none', md: 360 },
                            overflow: 'auto',
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: '#1a2b3c' }}>
                            Copy / paste content
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                            <SnippetBlock title="Copy" count={num(m.copy_attempt_count)} snippets={copySnips} />
                            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                            <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
                            <SnippetBlock title="Paste" count={num(m.paste_attempt_count)} snippets={pasteSnips} />
                        </Stack>
                    </Paper>
                </Stack>
                    </>
                )}
            </DialogContent>
        </Dialog>
        </SentryWrap>
    );
}
